import { StrictMode, Component, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import App from './App'
import { PreviewPage } from './pages/PreviewPage'
import { LandingPage } from './pages/LandingPage'
import { DashboardPage } from './pages/DashboardPage'
import { AdminPage } from './pages/AdminPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { JoinPage } from './pages/JoinPage'
import { ShowcasePage } from './pages/ShowcasePage'
import { ChangelogPage } from './pages/ChangelogPage'
import { ForRestaurantsPage, ForAgenciesPage, ForFreelancersPage, ForStartupsPage, ForEcommercePage, ForPortfoliosPage } from './pages/ForPage'
import { TemplatesPage } from './pages/TemplatesPage'
import { LoginPage } from './components/LoginPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { TermsPage } from './pages/TermsPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { CookiesPage } from './pages/CookiesPage'
import { AiSupportWidget } from './components/AiSupportWidget'
import './index.css'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    const { error } = this.state
    if (error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', fontFamily: 'sans-serif', color: '#333', background: '#f5f5f5', padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px' }}>⚠️</div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>Что-то пошло не так</h2>
          <p style={{ margin: 0, color: '#666', maxWidth: '400px' }}>Произошла неожиданная ошибка. Попробуйте перезагрузить страницу.</p>
          <button
            onClick={() => { this.setState({ error: null }); window.location.href = '/' }}
            style={{ padding: '10px 24px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
          >
            На главную
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function isAuthed() {
  const token = localStorage.getItem('bst-token')
  if (!token || token === 'undefined' || token === 'null') {
    localStorage.removeItem('bst-auth')
    localStorage.removeItem('bst-token')
    return false
  }
  return localStorage.getItem('bst-auth') === 'true'
}

function LoginRoute() {
  const navigate = useNavigate()
  if (isAuthed()) return <Navigate to="/dashboard" replace />
  return <LoginPage onLogin={() => navigate('/dashboard', { replace: true })} />
}

function EditorRoute() {
  if (!isAuthed()) return <Navigate to="/login" replace />
  return <><App /><AiSupportWidget /></>
}

function DashboardRoute() {
  if (!isAuthed()) return <Navigate to="/login" replace />
  return <><DashboardPage /><AiSupportWidget /></>
}

function AdminRoute() {
  if (!isAuthed()) return <Navigate to="/login" replace />
  return <><AdminPage /><AiSupportWidget /></>
}

function NotFoundPage() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', fontFamily: 'sans-serif', background: '#f9fafb', padding: '24px', textAlign: 'center' }}>
      <div style={{ fontSize: '72px', fontWeight: 900, color: '#e5e7eb', lineHeight: 1 }}>404</div>
      <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#111' }}>Страница не найдена</h1>
      <p style={{ margin: 0, color: '#666', maxWidth: '360px', fontSize: '14px' }}>Такой страницы не существует. Возможно, ссылка устарела или была введена с ошибкой.</p>
      <a href="/" style={{ marginTop: '8px', padding: '10px 28px', background: '#5b6af0', color: '#fff', borderRadius: '10px', textDecoration: 'none', fontWeight: 700, fontSize: '14px' }}>
        На главную
      </a>
    </div>
  )
}

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route path="/admin" element={<AdminRoute />} />
        <Route path="/editor" element={<EditorRoute />} />
        <Route path="/preview" element={<PreviewPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/join/:token" element={<JoinPage />} />
        <Route path="/showcase" element={<ShowcasePage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/for/restaurants" element={<ForRestaurantsPage />} />
        <Route path="/for/agencies" element={<ForAgenciesPage />} />
        <Route path="/for/freelancers" element={<ForFreelancersPage />} />
        <Route path="/for/startups" element={<ForStartupsPage />} />
        <Route path="/for/ecommerce" element={<ForEcommercePage />} />
        <Route path="/for/portfolios" element={<ForPortfoliosPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
)
